/**
 * leadsApi — Service-Layer fuer alle Backend-Endpoints.
 *
 * Kapselt JEDEN HTTP-Call. Komponenten und Stores duerfen NIE direkt eine
 * URL bilden — sie nutzen ausschliesslich diese Wrapper.
 *
 * Backend-Base: /programmes/leadmanagement-teamshops
 * Response-Format: { success: boolean, data?: T, error?: { code, message } }
 *
 * Nutzt @libero/core/services/api fuer 401-Auto-Refresh und CORS-Cookies.
 */

import { api } from '@libero/core/services/api';
import type { Lead, LeadEvent, LeadMail, LeadMailAttachment, LeadTask, LeadTemplate, LeadTemplateKey, LeadTemplateProfile, TemplateProfilesGrouped, UserMini, DashboardMetrics, ListenerStatus, UserSignature, TestSendResponse, SignatureProfile, ProgrammeSettings } from '../types';

// ── Bulk-Action-Typen (Phase 4 Plan 04 — ADMIN-03..06) ────────────────────
export type BulkActionType = 'add_tag' | 'remove_tag' | 'change_status' | 'assign_user' | 'delete';

export interface BulkActionRequest {
  leadIds: number[];
  action: BulkActionType;
  payload: {
    tag?: string;
    status?: string;
    userId?: number | null;
  };
}

export interface BulkActionResponse {
  successful: number;
  failed: Array<{ leadId: number; error: string }>;
}

const BASE = '/programmes/leadmanagement-teamshops';

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string; details?: unknown };
}

export interface ListLeadsParams {
  status?: string;
  tag?: string;
  assignedUserId?: number;
  search?: string;
  source?: string;
}

// ── Plan 06-04: Neuer Lead aus Telefonat ─────────────────────────────────
export interface CreateLeadWithCallBody {
  vereinName: string;
  vereinEmail?: string | null;
  vereinPhone?: string | null;
  vereinContactPerson?: string | null;
  callDate: string;
  callDurationMinutes: number;
  callNotes?: string | null;
}

function unwrap<T>(envelope: ApiEnvelope<T>, op: string): T {
  if (!envelope.success || envelope.data === undefined) {
    throw new Error(envelope.error?.message ?? `${op} fehlgeschlagen`);
  }
  return envelope.data;
}

export const leadsApi = {
  async list(params: ListLeadsParams = {}): Promise<Lead[]> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.tag) qs.set('tag', params.tag);
    if (params.assignedUserId != null) qs.set('assignedUserId', String(params.assignedUserId));
    if (params.search) qs.set('search', params.search);
    if (params.source) qs.set('source', params.source);
    const url = `${BASE}/leads${qs.toString() ? '?' + qs.toString() : ''}`;
    const res = await api.get<ApiEnvelope<Lead[]>>(url);
    return res.data.data ?? [];
  },

  async get(id: number): Promise<Lead> {
    const res = await api.get<ApiEnvelope<Lead>>(`${BASE}/leads/${id}`);
    return unwrap(res.data, 'getLead');
  },

  async create(body: Partial<Lead>): Promise<Lead> {
    const res = await api.post<ApiEnvelope<Lead>>(`${BASE}/leads`, body);
    return unwrap(res.data, 'createLead');
  },

  async update(id: number, body: Partial<Lead>): Promise<Lead> {
    const res = await api.patch<ApiEnvelope<Lead>>(`${BASE}/leads/${id}`, body);
    return unwrap(res.data, 'updateLead');
  },

  async remove(id: number): Promise<void> {
    await api.delete(`${BASE}/leads/${id}`);
  },

  async events(id: number): Promise<LeadEvent[]> {
    const res = await api.get<ApiEnvelope<LeadEvent[]>>(`${BASE}/leads/${id}/events`);
    return res.data.data ?? [];
  },

  // ── Plan 5.1-04: Mailverlauf ─────────────────────────────────────────
  async mails(id: number): Promise<LeadMail[]> {
    const res = await api.get<ApiEnvelope<LeadMail[]>>(`${BASE}/leads/${id}/mails`);
    return res.data.data ?? [];
  },

  // Anhang als Blob laden und Browser-Download ausloesen (2026-06-19).
  // Ueber den api-Client (NICHT plain <a href>), damit der 401-Auto-Refresh greift.
  async downloadAttachment(
    leadId: number,
    mailId: number,
    att: LeadMailAttachment,
  ): Promise<void> {
    const res = await api.get(
      `${BASE}/leads/${leadId}/mails/${mailId}/attachments/${att.id}`,
      { responseType: 'blob' },
    );
    const url = URL.createObjectURL(res.data as Blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || 'anhang';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  },

  // Excel-Export ALLER Leads (Vereinsname + E-Mail), unabhaengig von Board-Filtern
  // (Quick-Task 260707-k91). Blob-Download-Pattern wie downloadAttachment.
  async exportExcel(): Promise<void> {
    const res = await api.get(`${BASE}/leads/export-excel`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data as Blob);
    try {
      const dateStr = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `teamshop-leads-${dateStr}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  },

  async reply(
    id: number,
    body: {
      to: string;
      subject: string;
      body: string;
      bodyHtml?: string | null;
      signatureProfileId?: number | null;
      templateProfileId?: number | null;
    },
  ): Promise<{ simulated?: boolean; messageId?: string; message?: string }> {
    const res = await api.post<ApiEnvelope<{ simulated?: boolean; messageId?: string; message?: string }>>(
      `${BASE}/leads/${id}/reply`,
      body,
    );
    return res.data.data ?? {};
  },

  async addTag(id: number, tag: string): Promise<Lead> {
    const res = await api.post<ApiEnvelope<Lead>>(`${BASE}/leads/${id}/tags`, { tag });
    return unwrap(res.data, 'addTag');
  },

  async removeTag(id: number, tag: string): Promise<Lead> {
    const res = await api.delete<ApiEnvelope<Lead>>(
      `${BASE}/leads/${id}/tags/${encodeURIComponent(tag)}`,
    );
    return unwrap(res.data, 'removeTag');
  },

  async logCall(
    id: number,
    body: { calledAt?: string; durationMinutes: number; resultNote: string },
  ): Promise<LeadEvent> {
    const res = await api.post<ApiEnvelope<LeadEvent>>(`${BASE}/leads/${id}/call-log`, body);
    return unwrap(res.data, 'logCall');
  },

  // ── Plan 06-03: Auto-Complete-Suche fuer TelefonatModal ──────────────
  async search(q: string, limit = 8): Promise<Lead[]> {
    const qs = new URLSearchParams({ q, limit: String(limit) });
    const res = await api.get<ApiEnvelope<Lead[]>>(`${BASE}/leads/search?${qs}`);
    return res.data.data ?? [];
  },

  // ── Plan 06-04: Neuer Lead aus Telefonat anlegen ──────────────────────
  async createLeadWithCall(body: CreateLeadWithCallBody): Promise<Lead> {
    const res = await api.post<ApiEnvelope<Lead>>(`${BASE}/leads/create-with-call`, body);
    return unwrap(res.data, 'createLeadWithCall');
  },

  /**
   * User-Liste fuer "Zugewiesen an"-Dropdown.
   * Programm-eigener Endpoint (statt Core /api/users) — Non-Admin-User
   * koennen sonst keine Liste laden (Core verlangt manage:users).
   */
  async listUsers(): Promise<UserMini[]> {
    const res = await api.get<ApiEnvelope<UserMini[]>>(`${BASE}/users`);
    return res.data.data ?? [];
  },

  // ── Templates (Phase 4 Plan 02 — Mail-Template-Editor) ──────────────
  templates: {
    /** Alle 3 Mail-Vorlagen laden (erstkontakt, followup_1, followup_2). */
    async list(): Promise<LeadTemplate[]> {
      const res = await api.get<ApiEnvelope<LeadTemplate[]>>(`${BASE}/templates`);
      return res.data.data ?? [];
    },

    /** Einzelne Vorlage laden. */
    async get(key: string): Promise<LeadTemplate> {
      const res = await api.get<ApiEnvelope<LeadTemplate>>(`${BASE}/templates/${key}`);
      return unwrap(res.data, 'getTemplate');
    },

    /** Vorlage speichern (PUT — vollstaendiger Ersatz). */
    async update(
      key: string,
      body: { subject: string; bodyText: string; bodyHtml?: string | null },
    ): Promise<LeadTemplate> {
      const res = await api.put<ApiEnvelope<LeadTemplate>>(
        `${BASE}/templates/${key}`,
        body,
      );
      return unwrap(res.data, 'updateTemplate');
    },

    /** Test-Send an req.user.email (D-08). Plan 07-04: optionaler signatureProfileId.
     * BugFix 29.05.: optionaler templateProfileId — testet das gewaehlte Profil
     * statt der Default-Vorlage der Stage. */
    async testSend(
      key: string,
      body: { signatureProfileId?: number | null; templateProfileId?: number | null } = {},
    ): Promise<TestSendResponse> {
      const res = await api.post<ApiEnvelope<TestSendResponse>>(
        `${BASE}/templates/${key}/test-send`,
        body,
      );
      return unwrap(res.data, 'testSendTemplate');
    },
  },

  // ── Signatur (Phase 5.2 Plan 03 — D-04) ─────────────────────────────
  signature: {
    /** Eigene Signatur laden (200 mit signatureHtml='' wenn nicht gesetzt). */
    async getMine(): Promise<UserSignature> {
      const res = await api.get<ApiEnvelope<UserSignature>>(`${BASE}/signatures/me`);
      return unwrap(res.data, 'getMySignature');
    },
    /** Eigene Signatur speichern (Upsert). */
    async updateMine(signatureHtml: string): Promise<UserSignature> {
      const res = await api.put<ApiEnvelope<UserSignature>>(
        `${BASE}/signatures/me`,
        { signatureHtml },
      );
      return unwrap(res.data, 'updateMySignature');
    },
    // Hinweis: Bilder werden im TipTapEditor base64-inline in den HTML-Body
    // eingebettet — KEIN Server-Upload-Endpoint. Grund: Core mountet
    // /api/programmes/* mit authenticateToken (siehe core/backend/src/app.ts:305),
    // dadurch koennten Mail-Empfaenger gehostete Bilder nicht laden.
  },

  // ── Phase 07: Signatur-Profile (D-07) ────────────────────────────────
  signatureProfiles: {
    async list(): Promise<SignatureProfile[]> {
      const res = await api.get<ApiEnvelope<SignatureProfile[]>>(`${BASE}/signature-profiles/me`);
      return res.data.data ?? [];
    },
    async getDefault(): Promise<SignatureProfile | null> {
      const res = await api.get<ApiEnvelope<SignatureProfile | null>>(`${BASE}/signature-profiles/me/default`);
      return res.data.data ?? null;
    },
    async create(body: { name: string; signatureHtml: string; isDefault?: boolean }): Promise<SignatureProfile> {
      const res = await api.post<ApiEnvelope<SignatureProfile>>(`${BASE}/signature-profiles/me`, body);
      return unwrap(res.data, 'createSignatureProfile');
    },
    async update(id: number, body: { name: string; signatureHtml: string }): Promise<SignatureProfile> {
      const res = await api.put<ApiEnvelope<SignatureProfile>>(`${BASE}/signature-profiles/me/${id}`, body);
      return unwrap(res.data, 'updateSignatureProfile');
    },
    async setDefault(id: number): Promise<SignatureProfile> {
      const res = await api.put<ApiEnvelope<SignatureProfile>>(`${BASE}/signature-profiles/me/${id}/default`, {});
      return unwrap(res.data, 'setDefaultSignatureProfile');
    },
    async remove(id: number): Promise<void> {
      await api.delete(`${BASE}/signature-profiles/me/${id}`);
    },
  },

  // ── Phase 08 Plan 02: Mail-Vorlagen-Profile (D-07) ────────────────────
  templateProfiles: {
    /** Liste aller Vorlagen-Profile gruppiert nach templateKey. */
    async list(): Promise<TemplateProfilesGrouped> {
      const res = await api.get<ApiEnvelope<TemplateProfilesGrouped>>(
        `${BASE}/template-profiles`,
      );
      return res.data.data ?? { erstkontakt: [], followup_1: [], followup_2: [] };
    },
    /** Profile einer einzelnen Stage. */
    async byStage(templateKey: LeadTemplateKey): Promise<LeadTemplateProfile[]> {
      const res = await api.get<ApiEnvelope<LeadTemplateProfile[]>>(
        `${BASE}/template-profiles/by-stage/${templateKey}`,
      );
      return res.data.data ?? [];
    },
    /** Einzel-Lookup per ID. */
    async get(id: number): Promise<LeadTemplateProfile> {
      const res = await api.get<ApiEnvelope<LeadTemplateProfile>>(
        `${BASE}/template-profiles/${id}`,
      );
      return unwrap(res.data, 'getTemplateProfile');
    },
    /** Neues Profil anlegen. */
    async create(body: {
      templateKey: LeadTemplateKey;
      name: string;
      subject: string;
      bodyHtml?: string;
      bodyText?: string;
      isDefault?: boolean;
      /** Bug B (30.05.): pro Vorlage gespeicherte Sig (oder null = keine). */
      defaultSignatureProfileId?: number | null;
    }): Promise<LeadTemplateProfile> {
      const res = await api.post<ApiEnvelope<LeadTemplateProfile>>(
        `${BASE}/template-profiles`,
        body,
      );
      return unwrap(res.data, 'createTemplateProfile');
    },
    /** Profil-Felder updaten (name/subject/bodyHtml/bodyText/defaultSignatureProfileId). templateKey + isDefault hier verboten. */
    async update(
      id: number,
      body: {
        name?: string;
        subject?: string;
        bodyHtml?: string;
        bodyText?: string;
        /** Bug B (30.05.): null = Sig loeschen, Zahl = Sig setzen, undefined = unveraendert. */
        defaultSignatureProfileId?: number | null;
      },
    ): Promise<LeadTemplateProfile> {
      const res = await api.put<ApiEnvelope<LeadTemplateProfile>>(
        `${BASE}/template-profiles/${id}`,
        body,
      );
      return unwrap(res.data, 'updateTemplateProfile');
    },
    /** Als Default markieren (atomar pro Stage). */
    async setDefault(id: number): Promise<LeadTemplateProfile> {
      const res = await api.put<ApiEnvelope<LeadTemplateProfile>>(
        `${BASE}/template-profiles/${id}/default`,
        {},
      );
      return unwrap(res.data, 'setDefaultTemplateProfile');
    },
    /** Profil loeschen (mit Default-Reassign-Logik im Backend; HTTP 400 wenn letzte Vorlage einer Stage). */
    async remove(id: number): Promise<void> {
      await api.delete(`${BASE}/template-profiles/${id}`);
    },
  },

  // ── Dashboard (Phase 4 Plan 03 — KPI-Metriken) ──────────────────────
  dashboard: {
    /** Alle 5 Dashboard-KPIs in einer Request laden. Auto-Refresh alle 60s in DashboardPage. */
    async getMetrics(): Promise<DashboardMetrics> {
      const res = await api.get<ApiEnvelope<DashboardMetrics>>(`${BASE}/dashboard/metrics`);
      return unwrap(res.data, 'getDashboardMetrics');
    },
  },

  // ── Bulk-Actions (Phase 4 Plan 04 — ADMIN-03..06) ────────────────────
  bulk: {
    /** POST /leads/bulk-action — Fuehrt Bulk-Aktion fuer mehrere Leads aus. */
    async execute(req: BulkActionRequest): Promise<BulkActionResponse> {
      const res = await api.post<ApiEnvelope<BulkActionResponse>>(
        `${BASE}/leads/bulk-action`,
        req,
      );
      return unwrap(res.data, 'bulkAction');
    },
  },

  // ── Tasks (Phase 4 Plan 01 — Tasks/Follow-ups) ────────────────────────
  tasks: {
    /** Liste aller Tasks fuer einen Lead (offene + abgehakte). */
    async listForLead(leadId: number): Promise<LeadTask[]> {
      const res = await api.get<ApiEnvelope<LeadTask[]>>(
        `${BASE}/tasks?leadId=${leadId}`,
      );
      return res.data.data ?? [];
    },

    /** Aggregations-Liste fuer Board-Header-Panel: alle offenen Tasks mit dueDate <= upperIso. */
    async listByDue(upperIso: string): Promise<LeadTask[]> {
      const qs = new URLSearchParams({
        dueBefore: upperIso,
        completed: 'false',
      });
      const res = await api.get<ApiEnvelope<LeadTask[]>>(
        `${BASE}/tasks?${qs.toString()}`,
      );
      return res.data.data ?? [];
    },

    /** Neuen Task anlegen — Backend setzt task_created Event automatisch. */
    async create(body: {
      leadId: number;
      title: string;
      dueDate: string;
    }): Promise<LeadTask> {
      const res = await api.post<ApiEnvelope<LeadTask>>(`${BASE}/tasks`, body);
      return unwrap(res.data, 'createTask');
    },

    /** Task abhaken (oder zuruecksetzen). Backend setzt task_completed Event automatisch. */
    async setCompleted(taskId: number, completed: boolean): Promise<LeadTask> {
      const res = await api.patch<ApiEnvelope<LeadTask>>(
        `${BASE}/tasks/${taskId}`,
        { completed },
      );
      return unwrap(res.data, 'setCompleted');
    },

    /** Task loeschen. */
    async remove(taskId: number): Promise<void> {
      await api.delete(`${BASE}/tasks/${taskId}`);
    },
  },

  // ── Listener-Status (Phase 5 Plan 02 — ADMIN-07 Read-Only) ──────────
  listenerStatus: {
    /** GET /listener-status — liest aktuellen Listener-Job aus JobHistory. */
    async get(): Promise<ListenerStatus> {
      const res = await api.get<ApiEnvelope<ListenerStatus>>(`${BASE}/listener-status`);
      return unwrap(res.data, 'listenerStatus.get');
    },
  },

  // ── Betriebsmodus-Settings (01.06.) ─────────────────────────────────
  settings: {
    async get(): Promise<ProgrammeSettings> {
      const res = await api.get<ApiEnvelope<ProgrammeSettings>>(`${BASE}/settings`);
      return unwrap(res.data, 'settings.get');
    },
    async update(patch: Partial<Pick<ProgrammeSettings, 'mode' | 'testTimerMinutes' | 'allowlist'>>): Promise<ProgrammeSettings> {
      const res = await api.put<ApiEnvelope<ProgrammeSettings>>(`${BASE}/settings`, patch);
      return unwrap(res.data, 'settings.update');
    },
  },
};
